import React, {FC, SyntheticEvent, useCallback, useState} from 'react';
import {useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';

import {AttachmentModel} from '../../../../../../../../src/models/api/attachmentModel';
import {FrontDragEvent, Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {DragAndDropFileTypesEnum, DragAndDropVersionsEnum} from './dragAndDropConstants';
import {DragAndDropInnerV1} from './dragAndDropInnerV1';
import {DragAndDropInnerV2} from './dragAndDropInnerV2';

/*
 * Props.
 */

interface DragAndDropProps {
  onDropFile: HandlerOf<File>;
  onClearFile?: Handler;
  allowedFileTypes?: ReadonlyArray<DragAndDropFileTypesEnum>;
  attachment?: AttachmentModel;
  shouldSupportMultipleFiles?: boolean;
  isUploading?: boolean;
  hasError?: boolean;
  version?: DragAndDropVersionsEnum;
}

interface FileDragItem {
  files?: ReadonlyArray<File>;
}

/*
 * Component.
 */

export const DragAndDrop: FC<DragAndDropProps> = (props) => {
  const {
    allowedFileTypes,
    attachment,
    shouldSupportMultipleFiles,
    isUploading,
    hasError,
    version,
    onDropFile,
    onClearFile,
  } = props;

  const [inputKey, setInputKey] = useState(0);

  const [isFileHovering, dropRef] = useDrop<FileDragItem, void, boolean>({
    accept: [NativeTypes.FILE],
    drop: (item, monitor) => {
      if (isUploading) {
        return;
      }
      handleDrop(props, monitor.getItemType(), item);
    },
    collect: (monitor) => monitor.isOver(),
  });

  const onDropCapture = (e: FrontDragEvent) => {
    e.preventDefault();
  };

  const onChange = useCallback(
    async (event: SyntheticEvent<HTMLInputElement>) => {
      if (isUploading) {
        return;
      }

      // Only accept the first file, we should only get one
      const {files} = event.currentTarget;
      if (!files) {
        return;
      }
      const filteredFiles = filterForAllowedFiles(Array.from(files), allowedFileTypes);

      if (filteredFiles.length === 0) {
        return;
      }

      if (shouldSupportMultipleFiles) {
        filteredFiles.forEach((f) => onDropFile(f));
      } else {
        onDropFile(filteredFiles[0]);
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [allowedFileTypes, onDropFile, shouldSupportMultipleFiles, isUploading],
  );

  return (
    <div ref={dropRef} onDropCapture={onDropCapture}>
      {version === DragAndDropVersionsEnum.V2 && onClearFile ? (
        <DragAndDropInnerV2
          inputKey={inputKey}
          allowedFileTypes={allowedFileTypes}
          attachment={attachment}
          hasError={hasError}
          isFileHovering={isFileHovering}
          isUploading={isUploading}
          onChange={onChange}
          onClearFile={onClearFile}
        />
      ) : (
        <DragAndDropInnerV1
          hasError={hasError}
          isUploading={isUploading}
          isFileHovering={isFileHovering}
          inputKey={inputKey}
          onChange={onChange}
        />
      )}
    </div>
  );
};

/*
 * Helpers.
 */

function handleDrop(props: DragAndDropProps, type: unknown, item: FileDragItem) {
  const {shouldSupportMultipleFiles, allowedFileTypes, onDropFile} = props;

  switch (type) {
    case NativeTypes.FILE: {
      const {files} = item;
      if (!files) {
        return;
      }

      // Filter files incase we only allow certain file types
      const filteredFiles = filterForAllowedFiles(files, allowedFileTypes);
      if (filteredFiles.length === 0) {
        return;
      }

      if (shouldSupportMultipleFiles) {
        filteredFiles.forEach((f) => onDropFile(f));
      } else {
        onDropFile(filteredFiles[0]);
      }
      break;
    }
    default:
  }
}

const filterForAllowedFiles = (
  files: ReadonlyArray<File>,
  allowedFileTypes?: ReadonlyArray<DragAndDropFileTypesEnum>,
) => {
  if (!allowedFileTypes) {
    return files;
  }
  return files.filter((file) => allowedFileTypes.some((fileType) => file.type === fileType));
};
